// Project overview (RF-UI-09..12): preview (~62%) beside the project chat (~38%), with the
// "Work in progress" steps below. The preview shows the project's first registered target
// with its server-decided embed policy (live only when allowed); the chat talks to the project assistant; each step expands into its work tree
// (attempts, steps, calls, Gauntlet) and the footer shows usage only as reported. Mobile switches between
// Preview, Chat and Steps with a segmented control instead of compressing columns.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { Board, ProjectSummary, TaskCard } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getTask } from "../../api/endpoints.ts";
import type { TaskDurations } from "../../api/types.ts";
import { DurationsView, TaskTimeline } from "../../lib/taskFacts.tsx";
import type { AsyncState } from "../../lib/useAsync.ts";
import { readLocal, writeLocal } from "../../lib/storage.ts";
import { STEP_VIEW, stepStateOf, statusView, type StepState } from "../../lib/status.ts";
import { executionFacts } from "../../lib/taskExecution.ts";
import { KANBAN_PAGE_SIZE } from "./KanbanTab.tsx";
import { CardExecution, CardTimers } from "./TaskCardFacts.tsx";
import { Button, IconButton } from "../../components/Button.tsx";
import { ProjectChat } from "../../features/chat/ProjectChat.tsx";
import { TraceView, UsageLine } from "../../features/runs/TraceView.tsx";
import { getUsage } from "../../api/endpoints-wave2.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { getPreview } from "../../api/endpoints-wave3.ts";
import { PreviewFacts, PreviewStateBadge, PreviewSurface } from "../../features/browser/Preview.tsx";
import { SplitPane } from "../../components/SplitPane.tsx";
import { StatusIcon } from "../../components/Status.tsx";
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, HistoryIcon, MonitorIcon, RefreshIcon, SettingsIcon } from "../../components/icons.tsx";
import { ProjectInsights } from "../../features/wave5/ProjectInsights.tsx";
import { UnpublishedPreview, liveAppUrl, type PublicationContext } from "../../features/microsystem/Publication.tsx";
import { blockExplanation, blockTitle } from "../../features/blocks/explain.ts";

type MobilePanel = "preview" | "chat" | "steps";

function allTasks(board: Board | undefined): TaskCard[] {
  if (!board) return [];
  return [...board.columns.flatMap((c) => c.tasks), ...board.side_lane.flatMap((s) => s.tasks)];
}

function PreviewPanel({ projectId, publication }: { projectId: string; publication?: PublicationContext | undefined }) {
  const { t } = useI18n();
  // A succeeded publish registers the app as a preview target (D31): read the targets again when it lands.
  const published = publication?.publication.publish?.state === "succeeded";
  const preview = useAsync((signal) => getPreview(projectId, signal), [projectId, published]);
  const target = preview.data?.items[0] ?? null;
  const liveUrl = !target && publication ? liveAppUrl(publication.publication.publish, undefined) : null;
  return (
    <section className="card preview-panel" aria-labelledby="preview-title" data-testid="overview-preview">
      <div className="card-header">
        <MonitorIcon size={18} />
        <h2 id="preview-title" className="card-title">
          {t("overview.preview")}
        </h2>
        <span className="spacer" />
        {target ? <PreviewStateBadge target={target} /> : null}
        <Link to={`/projects/${projectId}/preview`} className="link-subtle">
          {t("overview.previewManage")}
        </Link>
      </div>
      {/* The URL strip only when there is a URL: the empty state below already says there is no preview. */}
      {target || liveUrl ? (
        <div className="preview-bar" data-testid="overview-preview-bar">
          <span className="preview-url" title={target?.url ?? liveUrl ?? undefined}>
            {target ? target.url : liveUrl}
          </span>
        </div>
      ) : null}
      {preview.loading ? (
        <LoadingBlock label={t("common.loading")} />
      ) : preview.error ? (
        <div className="card-body">
          <ErrorBanner error={preview.error} onRetry={preview.reload} title={t("preview.loadFailed")} />
        </div>
      ) : target ? (
        <>
          <PreviewFacts target={target} />
          <PreviewSurface target={target} />
        </>
      ) : publication ? (
        <div className="preview-body">
          <UnpublishedPreview projectId={projectId} {...publication} />
        </div>
      ) : (
        <div className="preview-body">
          <EmptyState icon={<MonitorIcon />} title={t("overview.previewUnavailableTitle")} headingLevel={3}>
            <p>{t("overview.previewUnavailableBody")}</p>
          </EmptyState>
        </div>
      )}
    </section>
  );
}

function StepRow({ task, projectId }: { task: TaskCard; projectId: string }) {
  const { t, formatDateTime } = useI18n();
  const [open, setOpen] = useState(false);
  const [durations, setDurations] = useState<TaskDurations | undefined>(undefined);
  const state = stepStateOf(task.status);
  const view = STEP_VIEW[state];

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Durations are real server facts (GET /v1/tasks/{id}); shown only when reported.
    getTask(task.id)
      .then((d) => !cancelled && setDurations(d.durations))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, task.id, task.version]);

  const detailsId = `step-${task.id}-details`;
  return (
    <li className={`step-row step-row--${view.tone}`} data-testid="step-row" data-task-number={task.number} data-step-state={state}>
      <div className="step-summary">
        <span className={`step-icon tone-${view.tone}`}>
          <StatusIcon kind={view.icon} size={16} />
        </span>
        <div className="step-main">
          <button type="button" className="step-toggle" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen((o) => !o)}>
            <span className="step-title">
              <span className="mono subtle">#{task.number}</span> <span className="user-content">{task.title}</span>
            </span>
            {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </button>
          {task.block_reason ? (
            // D37: the translated reason (the English one stays on the task modal under "Technical details").
            <p className={`step-reason tone-${view.tone}`} data-testid={`step-block-${task.number}`}>
              <strong>{blockTitle(t, task.block_reason)}</strong> — {blockExplanation(t, task.block_reason)}{" "}
              <Link to={`/projects/${task.project_id}/kanban?task=${task.id}`}>{t("block.seeWhatToDo")}</Link>
            </p>
          ) : task.status_reason ? (
            <p className={`step-reason tone-${view.tone}`}>{task.status_reason}</p>
          ) : null}
          <CardExecution task={task} />
          <CardTimers task={task} />
        </div>
        <div className="step-state">
          <span className={`tone-${view.tone}`}>{t(view.label)}</span>
          {t(statusView(task.status).label) !== t(view.label) ? <span className="subtle"> · {t(statusView(task.status).label)}</span> : null}
          {task.completed_at ? <span className="subtle"> · {formatDateTime(task.completed_at)}</span> : null}
        </div>
      </div>
      {open ? (
        <div id={detailsId} className="step-details">
          <DurationsView durations={durations} />
          {task.priority_reason ? <p className="subtle">{t("task.priorityReason", { reason: task.priority_reason })}</p> : null}
          <h3 className="subheading">{t("overview.attemptsTree")}</h3>
          <TraceView taskId={task.id} projectId={projectId} version={task.version} />
          <h3 className="subheading">{t("task.timeline")}</h3>
          <TaskTimeline taskId={task.id} version={task.version} />
          <Link to={`/projects/${projectId}/kanban?task=${task.id}`} className="link-subtle">
            {t("overview.openInKanban")} <ArrowRightIcon size={14} />
          </Link>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Work that needs a look comes first (running, failed, blocked, paused); waiting and finished work follow and are
 * shown one page at a time, so a large project never buries its failures under hundreds of waiting rows (RF-UI-09).
 */
const STEP_ORDER: Record<StepState, number> = { in_progress: 0, failed: 1, blocked: 2, paused: 3, waiting: 4, done: 5, cancelled: 6 };
const FIRST_PAGED = STEP_ORDER.waiting;

function StepsPanel({ project, board }: { project: ProjectSummary; board: AsyncState<Board> }) {
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(() => readLocal<boolean>("stepsCollapsed", false));
  const [shownTail, setShownTail] = useState(KANBAN_PAGE_SIZE);
  const tasks = allTasks(board.data)
    .filter((task) => task.status !== "backlog")
    .sort((a, b) => STEP_ORDER[stepStateOf(a.status)] - STEP_ORDER[stepStateOf(b.status)] || a.number - b.number);
  const head = tasks.filter((task) => STEP_ORDER[stepStateOf(task.status)] < FIRST_PAGED);
  const tail = tasks.filter((task) => STEP_ORDER[stepStateOf(task.status)] >= FIRST_PAGED);
  const visible = [...head, ...tail.slice(0, shownTail)];
  const remaining = tail.length - shownTail;
  const execution = executionFacts(board.error ? undefined : board.data);
  const failed = tasks.filter((task) => task.status === "failed").length;
  const toggle = () => {
    setCollapsed((c) => {
      writeLocal("stepsCollapsed", !c);
      return !c;
    });
  };
  return (
    <section className="card steps-panel" aria-labelledby="steps-title">
      <div className="card-header">
        <SettingsIcon size={18} />
        <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
          <h2 id="steps-title" className="card-title">
            {t("overview.steps")}
          </h2>
          <p className="subtle" style={{ fontSize: "var(--text-xs)" }}>
            {t("overview.stepsSummary", { active: execution.running, waiting: execution.waiting, failed, total: tasks.length })}
            {execution.unknown ? ` · ${t("kanban.card.executionUnknown")}` : null}
          </p>
        </div>
        {board.refreshing ? <span className="spinner subtle" aria-hidden="true" /> : null}
        <IconButton label={collapsed ? t("overview.expandSteps") : t("overview.collapseSteps")} aria-expanded={!collapsed} aria-controls="steps-list" onClick={toggle}>
          {collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
        </IconButton>
      </div>
      {!collapsed ? (
        <div id="steps-list">
          {board.loading ? (
            <LoadingBlock label={t("common.loading")} />
          ) : board.error && !board.data ? (
            <div className="card-body">
              <ErrorBanner error={board.error} onRetry={board.reload} />
            </div>
          ) : tasks.length === 0 ? (
            <EmptyState icon={<FileTextIcon />} title={t("overview.noStepsTitle")} headingLevel={3}>
              <p>{t("overview.noStepsBody")}</p>
              <div className="empty-state-actions">
                <Link to={`/projects/${project.id}/prds`} className="btn btn--sm">
                  {t("overview.importPrd")}
                </Link>
                <Link to={`/projects/${project.id}/kanban`} className="btn btn--sm">
                  {t("overview.openKanban")}
                </Link>
              </div>
            </EmptyState>
          ) : (
            <>
              <ol className="steps-list" aria-label={t("overview.steps")}>
                {visible.map((task) => (
                  <StepRow key={task.id} task={task} projectId={project.id} />
                ))}
              </ol>
              {remaining > 0 ? (
                <Button variant="ghost" className="kanban-load-more" onClick={() => setShownTail((n) => n + KANBAN_PAGE_SIZE)} data-testid="load-more-steps">
                  {t("kanban.loadMore", { count: Math.min(KANBAN_PAGE_SIZE, remaining), remaining })}
                </Button>
              ) : null}
            </>
          )}
          <div className="steps-footer">
            <Link to={`/observability?project=${project.id}`} className="link-subtle">
              <FileTextIcon size={14} />
              {t("overview.fullLogs")}
            </Link>
            <Link to={`/projects/${project.id}/runs`} className="link-subtle">
              <HistoryIcon size={14} />
              {t("overview.attemptHistory")}
            </Link>
            <span className="spacer" />
            <UsageBar projectId={project.id} version={board.data?.board_version} />
            <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={board.reload}>
              {t("common.refresh")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Project usage: tokens and cost only as reported, with coverage and precision (RF-OBS-03). */
function UsageBar({ projectId, version }: { projectId: string; version: string | undefined }) {
  const { t } = useI18n();
  const usage = useAsync((signal) => getUsage(projectId, signal), [projectId]);
  // A newer board refreshes the numbers in the background and the current ones stay on screen. A request in
  // flight is never aborted by a newer board (bursts of events would starve it): the change waits for it to finish.
  const seen = useRef(version);
  const pending = useRef(false);
  const busy = usage.loading || usage.refreshing;
  const { reload } = usage;
  useEffect(() => {
    if (seen.current !== version) {
      seen.current = version;
      pending.current = true;
    }
    if (pending.current && !busy) {
      pending.current = false;
      reload();
    }
  }, [version, busy, reload]);
  if (usage.loading) return null;
  if (usage.error || !usage.data)
    return (
      <span className="subtle" style={{ fontSize: "var(--text-xs)" }} data-testid="usage-bar">
        {t("overview.usageUnavailable")}
      </span>
    );
  return (
    <span className="usage-bar" data-testid="usage-bar">
      <UsageLine usage={usage.data.summary} />
      {usage.data.coverage && usage.data.coverage.calls_total > 0 ? (
        <span className={usage.data.coverage.truncated ? "badge badge--warning" : "subtle"} data-testid="usage-coverage" style={{ fontSize: "var(--text-xs)" }}>
          {usage.data.coverage.truncated
            ? t("usage.coverageTruncated", { n: usage.data.coverage.calls_included, total: usage.data.coverage.calls_total })
            : t("usage.coverageComplete", { total: usage.data.coverage.calls_total })}
        </span>
      ) : null}
    </span>
  );
}

export function OverviewTab({ project, board, publication }: { project: ProjectSummary; board: AsyncState<Board>; publication?: PublicationContext }) {
  const { t } = useI18n();
  const [split, setSplit] = useState(() => readLocal<number>("overviewSplit", 62));
  const [panel, setPanel] = useState<MobilePanel>("preview");
  const onSplit = (v: number) => {
    setSplit(v);
    writeLocal("overviewSplit", v);
  };
  const panels: { id: MobilePanel; label: string }[] = [
    { id: "preview", label: t("overview.preview") },
    { id: "chat", label: t("overview.chat") },
    { id: "steps", label: t("overview.steps") },
  ];
  return (
    <div className="overview" data-mobile-panel={panel}>
      <div className="segmented overview-switch" role="group" aria-label={t("overview.panels")}>
        {panels.map((p) => (
          <button key={p.id} type="button" aria-pressed={panel === p.id} onClick={() => setPanel(p.id)}>
            {p.label}
          </button>
        ))}
      </div>
      <SplitPane
        className="overview-top"
        value={split}
        onChange={onSplit}
        min={50}
        max={75}
        label={t("overview.resizeSplit")}
        start={<PreviewPanel projectId={project.id} publication={publication} />}
        end={<ProjectChat projectId={project.id} board={board.data} />}
      />
      <StepsPanel project={project} board={board} />
      <ProjectInsights projectId={project.id} />
    </div>
  );
}
